import React, { useState, useMemo } from 'react';
import { Route, Service, Schedule, Stop, StopsData } from '../types/ferry';
import { SeasonalWarning } from './SeasonalWarning';
import { FilterOptions } from './FilterPanel';
import { BookmarkButton } from './BookmarkButton';
import { BaseRouteCard } from './BaseRouteCard';
import { useLanguage } from '../contexts/LanguageContext';

export interface RouteResult {
  route: Route;
  service: Service;
  schedule: Schedule;
  fromStop: Stop;
  toStop: Stop;
  direction: string;
}

interface RouteResultsProps {
  results: RouteResult[];
  isLoading: boolean;
  error?: string;
  selectedDate: string;
  selectedTime: string;
  seasonalWarnings?: string[];
  stops: StopsData;
  fromStopId: string;
  toStopId: string;
}

export const RouteResults: React.FC<RouteResultsProps> = ({
  results,
  isLoading,
  error,
  selectedDate,
  selectedTime,
  seasonalWarnings = [],
  stops,
  fromStopId,
  toStopId
}) => {
  const { language, t } = useLanguage();
  const [filters, setFilters] = useState<FilterOptions>({
    vehicles: false,
    bicycles: false,
    passengers: true,
    hazardousMaterials: false,
    sortBy: 'departure',
    serviceTypes: []
  });


  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    let filtered = results.filter(result => {
      // Filter out hazardous materials only services unless explicitly requested
      const isHazardousOnly = result.schedule.special === 'hazardous_materials_only';
      if (isHazardousOnly && !filters.hazardousMaterials) return false;
      if (!isHazardousOnly && filters.hazardousMaterials && !result.service.hazardousMaterials) return false;
      
      // Apply capacity filters
      if (filters.vehicles && !result.service.vehicles) return false;
      if (filters.bicycles && !result.service.bicycles) return false;
      if (!filters.passengers && !result.service.passengers) return false;
      
      // Apply service type filters using stable service ids
      if (filters.serviceTypes.length > 0 && !filters.serviceTypes.includes(result.service.id)) {
        return false;
      }
      
      return true;
    });

    // Sort results
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'duration':
          return a.service.duration - b.service.duration;
        case 'arrival':
          return a.schedule.arrival.localeCompare(b.schedule.arrival);
        case 'departure':
        default:
          return a.schedule.departure.localeCompare(b.schedule.departure);
      }
    });

    return filtered;
  }, [results, filters]);

  // Get available service types for filter
  const availableServiceTypes = useMemo(() => {
    const types = new Map<string, string>();
    results.forEach(({ service }) => {
      types.set(service.id, service.name);
    });
    return Array.from(types.entries()).map(([id, name]) => ({ id, name }));
  }, [results]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-2 text-gray-600 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>{t.searchingRoutes}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className={`text-red-800 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>{error}</p>
        </div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="space-y-4">
        <SeasonalWarning warnings={seasonalWarnings} />
        <div className="text-center py-12">
          <svg className="w-12 h-12 text-gray-900 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.5-.885-6.126-2.342C5.137 11.774 5 10.907 5 10V8a7.963 7.963 0 016.126-2.342" />
          </svg>
          <h3 className={`text-lg font-medium text-gray-900 mb-2 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>{t.noRoutesFound}</h3>
          <p className={`text-gray-600 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>{t.adjustTime}</p>
        </div>
      </div>
    );
  }


  return (
    <div className="space-y-4">
      <SeasonalWarning warnings={seasonalWarnings} />
      
      {/* Filter and Sort Panel */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative">
          {/* Left Column - Filters */}
          <div className="space-y-4">
            {/* Transport Type Section */}
            <div>
              <h4 className={`text-sm font-semibold text-gray-900 mb-3 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                Transport Type
              </h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.vehicles}
                    onChange={(e) => setFilters(prev => ({ ...prev, vehicles: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    Vehicles allowed
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.bicycles}
                    onChange={(e) => setFilters(prev => ({ ...prev, bicycles: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    Bicycles allowed
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.hazardousMaterials}
                    onChange={(e) => setFilters(prev => ({ ...prev, hazardousMaterials: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    Hazardous materials
                  </span>
                </label>
              </div>
            </div>

            {/* Service Type Section */}
            {availableServiceTypes.length > 1 && (
              <div>
                <h4 className={`text-sm font-semibold text-gray-900 mb-3 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                  Service Type
                </h4>
                <div className="space-y-2">
                  {availableServiceTypes.map((serviceType) => (
                    <label key={serviceType.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.serviceTypes.includes(serviceType.id)}
                        onChange={(e) => {
                          const updatedTypes = e.target.checked
                            ? [...filters.serviceTypes, serviceType.id]
                            : filters.serviceTypes.filter(type => type !== serviceType.id);
                          setFilters(prev => ({ ...prev, serviceTypes: updatedTypes }));
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                      />
                      <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                        {serviceType.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Vertical Divider - Hidden on Mobile */}
          <div className="hidden lg:block absolute left-1/2 top-4 bottom-4 w-px bg-gray-200"></div>

          {/* Right Column - Sort Options */}
          <div className="space-y-4 lg:pl-6">
            <div>
              <h4 className={`text-sm font-semibold text-gray-900 mb-3 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                Sort Results
              </h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sortBy"
                    value="departure"
                    checked={filters.sortBy === 'departure'}
                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as 'departure' | 'duration' | 'arrival' }))}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    By departure time
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sortBy"
                    value="duration"
                    checked={filters.sortBy === 'duration'}
                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as 'departure' | 'duration' | 'arrival' }))}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    By duration
                  </span>
                </label>
                
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="sortBy"
                    value="arrival"
                    checked={filters.sortBy === 'arrival'}
                    onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value as 'departure' | 'duration' | 'arrival' }))}
                    className="border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className={`text-sm text-gray-700 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                    By arrival time
                  </span>
                </label>
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="pt-4">
              <button
                onClick={() => {
                  setFilters({
                    vehicles: false,
                    bicycles: false,
                    passengers: true,
                    hazardousMaterials: false,
                    sortBy: 'departure',
                    serviceTypes: []
                  });
                }}
                className={`text-sm text-blue-600 hover:text-blue-800 font-medium ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}
                disabled={!filters.vehicles && !filters.bicycles && !filters.hazardousMaterials && filters.serviceTypes.length === 0 && filters.sortBy === 'departure'}
              >
                Clear all filters
              </button>
              
              {/* Active Filter Count */}
              {(filters.vehicles || filters.bicycles || filters.hazardousMaterials || filters.serviceTypes.length > 0) && (
                <div className={`text-xs text-gray-500 mt-1 ${language === 'zh' ? 'font-chinese-sans' : 'font-sans'}`}>
                  Active: {
                    [filters.vehicles, filters.bicycles, filters.hazardousMaterials].filter(Boolean).length + 
                    filters.serviceTypes.length
                  } filter{([filters.vehicles, filters.bicycles, filters.hazardousMaterials].filter(Boolean).length + filters.serviceTypes.length) !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Route Results List */}
      <div className="space-y-4">
          {filteredAndSortedResults.map((result, index) => (
            <BaseRouteCard
              key={index}
              routeResult={result}
              selectedDate={selectedDate}
              language={language}
              t={t}
              actionButton={
                <BookmarkButton 
                  routeResult={result} 
                  selectedDate={selectedDate}
                  size="md"
                />
              }
            />
          ))}
      </div>
    </div>
  );
};
